import type {ChangeEvent} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {P} from 'UI/components/layout/Text';

import {FadeInAnimation} from 'UI/animations/shared';
import {HintAnimation} from 'UI/animations/textInput';

import {useTextInput} from './useTextInput';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * The `ComponentProps` interface defines the properties for the `TextInput` component.
 * This interface provides comprehensive configuration options for text input functionality including validation, accessibility, and user interaction controls.
 * It supports various input types and integrates with form handling systems through callback functions and validation mechanisms.
 */
interface ComponentProps {
    className?: string;
    /**
     * Optional hint text displayed below the input field to provide additional guidance or information to the user.
     */
    hint?: string;
    /**
     * The initial value that will be displayed in the input field when the component first renders.
     */
    initialValue?: string;
    /**
     * Determines whether the input field is disabled, preventing any user interaction or input.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Determines whether the input field is in readonly mode, allowing users to see the value but preventing modifications.
     *
     * @default false
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the input field is required for form submission, typically displayed with an asterisk indicator.
     *
     * @default false
     */
    isRequired?: boolean;
    /**
     * Optional label text that will be displayed above the input field to identify its purpose or expected content.
     */
    label?: string;
    /**
     * The name attribute for the input field, which is essential for form data handling and submission processes.
     */
    name: string;
    /**
     * Optional callback function that is triggered whenever the input value changes, receiving the change event as a parameter.
     */
    onChange?(e: ChangeEvent<HTMLInputElement>): void;
    /**
     * Optional placeholder text that appears inside the input field when it's empty, providing hints about the expected input format.
     */
    placeholder?: string;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'TextInput'
     */
    testId?: string;
    /**
     * Specifies the type of input field, which affects the virtual keyboard on mobile devices and browser validation behavior.
     *
     * @default 'text'
     */
    type?: 'date' | 'email' | 'tel' | 'text';
    /**
     * Optional validation function that receives the input value and returns an error message string if validation fails, or null/undefined if valid.
     */
    validation?: (ValidationFunc | undefined);
}

/**
 * The `TextInput` component provides a comprehensive text input field with advanced features including validation, error handling, and accessibility support.
 * This component integrates with MobX for reactive state management and includes animated hint/error messages for enhanced user experience.
 * It supports various input types (text, email, tel, date) and provides customizable styling through the theme system.
 *
 * @param props              The component props.
 * @param props.hint         Optional hint text displayed below the input field to guide the user.
 * @param props.initialValue The initial value of the input field upon component rendering.
 * @param props.isDisabled   Determines if the input field is disabled, preventing user interaction.
 * @param props.isReadonly   Determines if the input field is readonly, allowing users to see but not modify the value.
 * @param props.isRequired   Specifies if the input field is required for form submission, enforcing a value to be entered.
 * @param props.label        Optional label text displayed above the input field for identification.
 * @param props.name         The name attribute of the input field, important for form submission and data handling.
 * @param props.onChange     Callback function executed when the input's value changes.
 * @param props.placeholder  Optional placeholder text displayed inside the input field when empty.
 * @param props.testId       Unique identifier for testing purposes.
 * @param props.type         Specifies the type of input (date, email, tel, text).
 * @param props.validation   Optional validation function to apply to the input value.
 * @returns The TextInput component JSX with label, input field, and animated hint/error messages.
 *
 * @example
 * ```tsx
 * <TextInput
 *     name="email"
 *     label="Email Address"
 *     type="email"
 *     placeholder="Enter your email"
 *     isRequired={true}
 *     hint="We'll never share your email"
 *     validation={(value) => value.includes('@') ? null : 'Please enter a valid email'}
 *     testId="email-input"
 * />
 * ```
 */
const TextInput = observer(({
    className,
    hint,
    initialValue = '',
    isDisabled = false,
    isReadonly = false,
    isRequired = false,
    label,
    name,
    onChange = () => {},
    placeholder,
    testId = 'TextInput',
    type = 'text',
    validation
}: ComponentProps) => {
    const {
        colors,
        error,
        fieldRef,
        formId,
        handleChange,
        value
    } = useTextInput(name, initialValue, validation, onChange);

    return (
        <Wrapper className={className} data-cy={testId} variants={FadeInAnimation}>
            {label && (
                <>
                    <Label htmlFor={`${formId}-${name}`}>{label}{isRequired ? '*' : ''}</Label>
                    <Spacer y={2} />
                </>
            )}
            <Input
                ref={fieldRef}
                disabled={isDisabled}
                id={`${formId}-${name}`}
                name={name}
                placeholder={placeholder}
                readOnly={isReadonly}
                required={isRequired}
                type={type}
                value={value}
                onChange={handleChange}
            />
            <AnimatePresence initial={false} mode="sync">
                {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                {(hint || error) && (
                    <ExtraHint animate="enter" exit="exit" initial="initial" variants={HintAnimation}>
                        <Spacer y={2} />
                        {(hint && !error) && (
                            <P>{hint}</P>
                        )}
                        {error && (
                            <P><Color $color={colors.errorColor}>{error}</Color></P>
                        )}
                    </ExtraHint>
                )}
            </AnimatePresence>
        </Wrapper>
    );
});

TextInput.displayName = 'TextInput';

export {TextInput};

const Wrapper = styled(motion.div)`
    width: 100%;
`;

const Label = P.withComponent('label');

const Input = styled.input`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border: 1px solid ${({theme}) => theme.colors.darkBorder};
    border-radius: 12px;
    color: ${({theme}) => theme.colors.textPrimary};
    font-family: ${({theme}) => theme.fonts.Montserrat};
    font-size: 1.6rem;
    height: 4.8rem;
    outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 0);
    padding: 0 ${spacing(4)};
    transition: border-color 0.2s ease-in-out, outline 0.2s ease-in-out, background-color 0.2s ease-in-out;
    transition: color 0.2s ease-in-out;
    width: 100%;

    &::placeholder {
        color: ${({theme}) => theme.colors.textNeutral};
    }

    &:user-invalid {
        background-color: hsl(from ${({theme}) => theme.colors.errorColor} H S L / 0.3);
        border-color: ${({theme}) => theme.colors.errorColor};
        color: ${({theme}) => theme.colors.errorColor};
    }

    &:disabled, &:disabled::placeholder, &:read-only, &:read-only::placeholder {
        background-color: ${({theme}) => theme.colors.textNeutral};
        border-color: ${({theme}) => theme.colors.textNeutral};
        color: ${({theme}) => theme.colors.textNeutral};
    }

    &:focus-visible {
        border-color: ${({theme}) => theme.colors.tertiary};
        outline: 1px solid hsl(from ${({theme}) => theme.colors.tertiary} H S L / 1);
    }
`;

const ExtraHint = styled(motion.div)`
    height: 0;
    overflow: hidden;

    & p {
        font-size: 1.2rem;

        ${media('md')} {
            font-size: 1.4rem;
        }
    }
`;